import React from 'react'
import { Employee } from '@/types/workforce'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import {
  Mail,
  Building2,
  Briefcase,
  UserCircle,
  CheckCircle2
} from 'lucide-react'

interface RecipientCardProps {
  employee: Employee
  isSelected: boolean
  onSelect: (employee: Employee) => void
}

export function RecipientCard({
  employee,
  isSelected,
  onSelect
}: RecipientCardProps) {
  const name = employee.personalInfo?.name || 'Nome não informado'
  const nameParts = name.split(' ')
  const initials =
    nameParts.length >= 2
      ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`
      : `${nameParts[0][0]}${nameParts[0][1] || ''}`

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'bg-success/20 text-success'
      case 'INACTIVE':
        return 'bg-brand-gray/20 text-brand-gray'
      case 'TERMINATED':
        return 'bg-error/20 text-error'
      default:
        return 'bg-brand-gray/20 text-brand-gray'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'Ativo'
      case 'INACTIVE':
        return 'Inativo'
      case 'TERMINATED':
        return 'Demitido'
      case 'PENDING':
        return 'Pendente'
      default:
        return status
    }
  }

  return (
    <Card
      className={`
        card-hover p-4 cursor-pointer transition-all duration-200 relative
        ${isSelected
          ? 'ring-2 ring-brand-primary shadow-monumenta-brand bg-brand-dark-lighter'
          : 'hover:bg-brand-dark-lighter'
        }
      `}
      onClick={() => onSelect(employee)}
    >
      {/* Selection Indicator */}
      {isSelected && (
        <div className="absolute top-3 right-3">
          <CheckCircle2 className="w-6 h-6 text-brand-primary" />
        </div>
      )}

      <div className="flex items-start space-x-3">
        {/* Avatar */}
        <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-monumenta">
          <span className="text-sm font-bold text-brand-dark font-display">
            {initials.toUpperCase()}
          </span>
        </div>

        {/* Employee Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-display text-base font-semibold text-white truncate">
              {name}
            </h3>
            <Badge className={getStatusColor(employee.status)}>
              {getStatusLabel(employee.status)}
            </Badge>
          </div>

          {/* Compact Info Grid */}
          <div className="space-y-1 text-sm text-brand-gray">
            <div className="flex items-center gap-2">
              <Mail className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{employee.email || 'N/A'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Briefcase className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">
                {employee.currentEngagement?.roleName || 'N/A'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">
                {employee.currentEngagement?.departmentName || 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
